// /opt/ptg01-v1/front-extranet/components/auth/AuthShell.tsx
// Gabarit des écrans d'inscription et de vérification (R1-R3) — pages publiques, hors sidebar
// et hors bottom tab bar (exception CONVENTIONS.md § 6, comme /auth/*).
// Desktop : carte `max-w-xl` centrée. Mobile : plein écran, sans marge latérale perdue.
'use client';

import React from 'react';
import { NexviaLogo } from '@nexvia/ui';

interface Props {
    /** Icône Lucide affichée dans la pastille de titre. */
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    subtitle?: React.ReactNode;
    children: React.ReactNode;
    footer?: React.ReactNode;
    testId?: string;
}

export default function AuthShell({ icon: Icon, title, subtitle, children, footer, testId }: Props) {
    return (
        <div className="min-h-[100dvh] bg-white flex flex-col items-center px-4 py-8 sm:py-12">
            <div className="w-full max-w-xl flex flex-col gap-6" data-testid={testId}>
                <div className="flex justify-center"><NexviaLogo /></div>

                <div className="bg-white border-2 border-[#000091]/25 rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-6 sm:p-8">
                    <div className="flex flex-col items-center text-center mb-6">
                        <div className="w-12 h-12 rounded-full bg-[#000091]/[0.06] border border-[#000091]/15 flex items-center justify-center mb-3">
                            <Icon className="w-5 h-5 text-[#000091]" />
                        </div>
                        <h1 className="text-[20px] font-bold text-slate-900">{title}</h1>
                        {subtitle && <div className="text-[13px] text-black/60 mt-1.5">{subtitle}</div>}
                    </div>
                    {children}
                </div>

                {footer}

                <p className="text-center text-[11px] text-black/60">
                    &copy; {new Date().getFullYear()} Nexvia Portage SAS — Données hébergées en France
                </p>
            </div>
        </div>
    );
}

/** Champ de formulaire : libellé + contrôle + message d'erreur. `text-[16px]` évite le zoom iOS. */
export function Field({ label, htmlFor, error, hint, children }: {
    label: string; htmlFor?: string; error?: string | null; hint?: string; children: React.ReactNode;
}) {
    return (
        <div>
            <label htmlFor={htmlFor} className="block text-[12px] font-semibold text-black/75 mb-1.5">{label}</label>
            {children}
            {hint && !error && <p className="text-[11px] text-black/60 mt-1">{hint}</p>}
            {error && <p className="text-[11px] text-rose-700 mt-1" role="alert">{error}</p>}
        </div>
    );
}

export const INPUT_CLASS =
    'w-full px-3 h-12 bg-white border border-slate-300 rounded-lg text-slate-900 text-[16px] ' +
    'placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#000091] focus:border-[#000091] transition-all';

export const INPUT_ERROR_CLASS = 'border-rose-400 focus:ring-rose-500 focus:border-rose-500';

export const PRIMARY_BUTTON_CLASS =
    'w-full h-12 rounded-lg bg-[#000091] hover:bg-[#1e40af] text-white text-[14px] font-semibold ' +
    'transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

/** Bandeau d'erreur ou d'information au-dessus d'un formulaire. */
export function Banner({ tone, children }: { tone: 'error' | 'success' | 'info'; children: React.ReactNode }) {
    const cls = tone === 'error'
        ? 'bg-rose-50 border-rose-200 text-rose-700'
        : tone === 'success'
            ? 'bg-green-50 border-green-200 text-green-700'
            : 'bg-[#000091]/[0.04] border-[#000091]/20 text-[#000091]';
    return (
        <div className={`mb-5 p-3 border rounded-lg text-[13px] ${cls}`} role="alert">{children}</div>
    );
}
